import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, User, Car, Receipt, Loader2, Upload, FileText, Trash2, Download, Pencil, HandshakeIcon } from 'lucide-react';
import { numberToTextRo } from '@/utils/numberToTextRo';
import { CUILookupInput } from '@/components/CUILookupInput';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const statusLabels = { pending: 'In asteptare', documents_missing: 'Documente lipsa', in_processing: 'In procesare', at_city_hall: 'La Primarie', at_rar: 'La RAR', at_anaf: 'La ANAF', at_police: 'La Politie', completed: 'Finalizat', rejected: 'Respins' };

const DOCUMENT_TYPES = [
  'Factura achizitie',
  'Factura achizitie vizata',
  'Certificat fiscal',
  'CIV',
  'Talon',
  'Documente externe',
  'Certificat de autenticitate',
  'CUI/CI'
];

const ILFOV_LOCALITIES = [
  "1 Decembrie", "Afumati", "Balotesti", "Berceni", "Bragadiru",
  "Branesti", "Buftea", "Cernica", "Chiajna", "Chitila",
  "Ciolpani", "Ciorogirla", "Clinceni", "Copaceni", "Corbeanca",
  "Cornetu", "Darasti-Ilfov", "Dascalu", "Dobroesti", "Domnesti",
  "Dragomiresti-Vale", "Ganeasa", "Glina", "Gradistea", "Gruiu",
  "Jilava", "Magurele", "Moara Vlasiei", "Mogosoaia", "Nuci",
  "Otopeni", "Pantelimon", "Peris", "Petrachioaia", "Popesti-Leordeni",
  "Snagov", "Stefanestii de Jos", "Tunari", "Vidra", "Voluntari"
];

export default function AdminOrderDetail() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [billingData, setBillingData] = useState(null);
  const [billingInvoiceType, setBillingInvoiceType] = useState('normal');
  const [billingServices, setBillingServices] = useState([]);
  const [billingExchangeRate, setBillingExchangeRate] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [currentVehicleId, setCurrentVehicleId] = useState(null);
  const [generatingDoc, setGeneratingDoc] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templates, setTemplates] = useState([]);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [selectedFolderType, setSelectedFolderType] = useState('');
  const [generatingFolder, setGeneratingFolder] = useState(false);
  const [invoiceLines, setInvoiceLines] = useState([]);

  // Edit order state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    status: '',
    notes: '',
    deadline: '',
    advance_paid: ''
  });
  const [editClient, setEditClient] = useState({
    name: '', cnp_cui: '', id_document: '', administrator: '', email: '', phone: '', address: '', client_type: ''
  });
  const [editVehicle, setEditVehicle] = useState({
    vin: '', brand: '', model: '', plate_number: '', year: '', serie_motor: '', capacitate_cilindrica: ''
  });
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [allClients, setAllClients] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);
  const [editContract, setEditContract] = useState({
    buyer_type: 'persoana_fizica',
    buyer_name: '',
    buyer_cnp_cui: '',
    buyer_id_document: '',
    buyer_address: '',
    localitate: '',
    pret_vanzare: '',
    pret_vanzare_moneda: 'EUR',
    pret_vanzare_text: ''
  });

  const editPretText = useMemo(() => {
    const num = parseFloat(editContract.pret_vanzare);
    if (!num || num <= 0) return '';
    return `${numberToTextRo(num)} ${editContract.pret_vanzare_moneda}`;
  }, [editContract.pret_vanzare, editContract.pret_vanzare_moneda]);

  const folderTypes = [
    { value: 'declarare_vehicul_primarie', label: '1. Declarare vehicul primarie' },
    { value: 'radiere_vehicul_primarie', label: '2. Radiere vehicul primarie' },
    { value: 'inmatriculare_politie', label: '3. Inmatriculare politie' },
    { value: 'certificat_anaf', label: '4. Certificat ANAF' }
  ];

  useEffect(() => { fetchOrder(); fetchTemplates(); }, [orderId]);
  useEffect(() => { if (order) fetchBillingData(); }, [order?.order_id]);

  // Detecteaza daca comanda are un serviciu care necesita date contract
  const orderHasContractService = React.useMemo(() => {
    if (!order) return false;
    for (const veh of (order.vehicles || [])) {
      // Verifica prin service_details (populat la detaliu order)
      for (const sd of (veh.service_details || [])) {
        if (sd.service_id === 'srv_contract') return true;
        if (sd.name && sd.name.toLowerCase().includes('contract')) return true;
      }
      // Fallback: verifica prin services IDs + billingServices
      for (const srvId of (veh.services || [])) {
        const sid = typeof srvId === 'string' ? srvId : srvId?.service_id;
        if (sid === 'srv_contract') return true;
        const srv = billingServices.find(s => s.service_id === sid);
        if (srv && srv.name && srv.name.toLowerCase().includes('contract')) return true;
      }
    }
    return false;
  }, [order, billingServices]);

  const showContractSection = orderHasContractService || !!order?.contract_data;

  const fetchBillingData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const [billingRes, srvRes, rateRes] = await Promise.all([
        axios.get(API + '/admin/billing/order/' + orderId, { headers: { Authorization: 'Bearer ' + token }, withCredentials: true }),
        axios.get(API + '/services', { headers: { Authorization: 'Bearer ' + token }, withCredentials: true }),
        axios.get(API + '/exchange-rate', { headers: { Authorization: 'Bearer ' + token }, withCredentials: true })
      ]);
      setBillingData(billingRes.data);
      setBillingServices(srvRes.data.filter(s => s.is_active));
      setBillingExchangeRate(rateRes.data.eur_ron_rate_with_margin);
    } catch { /* silent */ }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(API + '/admin/templates', { headers: { Authorization: 'Bearer ' + token }, withCredentials: true });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Eroare la incarcarea template-urilor');
    }
  };

  const initInvoiceLines = () => {
    if (!order || !billingServices.length || !billingExchangeRate) return;
    const lines = [];
    for (const veh of (order.vehicles || [])) {
      for (const srvId of (veh.services || [])) {
        const srv = billingServices.find(s => s.service_id === srvId);
        const priceRon = srv ? Math.round(srv.price * billingExchangeRate * 100) / 100 : 0;
        lines.push({
          service_id: srvId,
          description: srv ? srv.name : srvId,
          quantity: 1,
          unit_price_ron: priceRon
        });
      }
    }
    setInvoiceLines(lines);
  };

  const updateInvoiceLine = (index, field, value) => {
    setInvoiceLines(prev => prev.map((line, i) => i === index ? { ...line, [field]: value } : line));
  };

  const invoiceTotal = invoiceLines.reduce((sum, l) => sum + (parseFloat(l.unit_price_ron) || 0) * (parseFloat(l.quantity) || 0), 0);
  const invoiceTotalEur = billingExchangeRate ? Math.round(invoiceTotal / billingExchangeRate * 100) / 100 : 0;

  const fetchOrder = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(API + '/admin/orders/' + orderId, { headers: { Authorization: 'Bearer ' + token }, withCredentials: true });
      setOrder(response.data);
    } catch (error) {
      toast.error('Eroare');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllClients = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(API + '/admin/clients', { headers: { Authorization: 'Bearer ' + token }, withCredentials: true });
      setAllClients(response.data);
    } catch (error) { console.error('Error fetching clients'); }
  };

  const fetchAllVehicles = async (clientId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = clientId ? { client_id: clientId } : {};
      const response = await axios.get(API + '/admin/vehicles', { headers: { Authorization: 'Bearer ' + token }, withCredentials: true, params });
      setAllVehicles(response.data);
    } catch (error) { console.error('Error fetching vehicles'); }
  };

  const onClientSwitch = (clientId) => {
    setSelectedClientId(clientId);
    const client = allClients.find(c => c.client_id === clientId);
    if (client) {
      setEditClient({
        name: client.name || '', cnp_cui: client.cnp_cui || '', id_document: client.id_document || '',
        administrator: client.administrator || '',
        email: client.email || '', phone: client.phone || '', address: client.address || '',
        client_type: client.client_type || 'persoana_fizica'
      });
      fetchAllVehicles(clientId);
      setSelectedVehicleId('');
    }
  };

  const onVehicleSwitch = (vehicleId) => {
    setSelectedVehicleId(vehicleId);
    const veh = allVehicles.find(v => v.vehicle_id === vehicleId);
    if (veh) {
      setEditVehicle({
        vin: veh.vin || '', brand: veh.brand || '', model: veh.model || '',
        plate_number: veh.plate_number || '', year: veh.year || '',
        serie_motor: veh.serie_motor || '', capacitate_cilindrica: veh.capacitate_cilindrica || ''
      });
    }
  };

  const openEditDialog = () => {
    if (!order) return;
    setEditForm({
      status: order.status || 'pending',
      notes: order.notes || '',
      deadline: order.deadline || '',
      advance_paid: order.advance_paid || 0
    });
    setSelectedClientId(order.client_id || '');
    setSelectedVehicleId(order.vehicles?.[0]?.vehicle_id || '');
    setEditClient({
      name: order.client?.name || '',
      cnp_cui: order.client?.cnp_cui || '',
      id_document: order.client?.id_document || '',
      administrator: order.client?.administrator || '',
      email: order.client?.email || '',
      phone: order.client?.phone || '',
      address: order.client?.address || '',
      client_type: order.client?.client_type || 'persoana_fizica'
    });
    const veh = order.vehicles?.[0]?.vehicle_details || {};
    setEditVehicle({
      vin: veh.vin || '',
      brand: veh.brand || '',
      model: veh.model || '',
      plate_number: veh.plate_number || '',
      year: veh.year || '',
      serie_motor: veh.serie_motor || '',
      capacitate_cilindrica: veh.capacitate_cilindrica || ''
    });
    fetchAllClients();
    fetchAllVehicles(order.client_id);
    if (order.contract_data) {
      setEditContract({
        buyer_type: order.contract_data.buyer_type || 'persoana_fizica',
        buyer_name: order.contract_data.buyer_name || '',
        buyer_cnp_cui: order.contract_data.buyer_cnp_cui || '',
        buyer_id_document: order.contract_data.buyer_id_document || '',
        buyer_address: order.contract_data.buyer_address || '',
        localitate: order.contract_data.localitate || '',
        pret_vanzare: order.contract_data.pret_vanzare || '',
        pret_vanzare_moneda: order.contract_data.pret_vanzare_moneda || 'EUR',
        pret_vanzare_text: order.contract_data.pret_vanzare_text || ''
      });
    }
    setEditDialogOpen(true);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        status: editForm.status,
        notes: editForm.notes,
        deadline: editForm.deadline || null,
        advance_paid: parseFloat(editForm.advance_paid) || 0,
        client_id: selectedClientId,
        new_vehicle_id: selectedVehicleId !== (order.vehicles?.[0]?.vehicle_id || '') ? selectedVehicleId : undefined,
        client_updates: {
          name: editClient.name,
          cnp_cui: editClient.cnp_cui,
          id_document: editClient.id_document,
          email: editClient.email,
          phone: editClient.phone,
          address: editClient.address,
          client_type: editClient.client_type,
          administrator: editClient.administrator || ''
        },
        vehicle_updates: {
          vin: editVehicle.vin,
          brand: editVehicle.brand,
          model: editVehicle.model,
          plate_number: editVehicle.plate_number,
          year: editVehicle.year ? parseInt(editVehicle.year) : null,
          serie_motor: editVehicle.serie_motor,
          capacitate_cilindrica: editVehicle.capacitate_cilindrica ? parseInt(editVehicle.capacitate_cilindrica) : null
        }
      };

      if (showContractSection) {
        payload.contract_data = {
          buyer_type: editContract.buyer_type,
          buyer_name: editContract.buyer_name,
          buyer_cnp_cui: editContract.buyer_cnp_cui,
          buyer_id_document: editContract.buyer_id_document || '',
          buyer_address: editContract.buyer_address || '',
          localitate: editContract.localitate,
          pret_vanzare: parseFloat(editContract.pret_vanzare) || 0,
          pret_vanzare_moneda: editContract.pret_vanzare_moneda,
          pret_vanzare_text: editPretText || editContract.pret_vanzare_text
        };
      }

      await axios.put(API + '/admin/orders/' + orderId, payload, {
        headers: { Authorization: 'Bearer ' + token },
        withCredentials: true
      });

      toast.success('Comanda actualizata cu succes');
      setEditDialogOpen(false);
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const updateVehicleStatus = async (vehicleId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(API + '/admin/orders/' + orderId + '/vehicles/' + vehicleId + '/status', { status: newStatus }, { headers: { Authorization: 'Bearer ' + token }, withCredentials: true });
      toast.success('Status actualizat');
      fetchOrder();
    } catch (error) {
      toast.error('Eroare');
    }
  };

  const createInvoice = async () => {
    setCreatingInvoice(true);
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { headers: { Authorization: 'Bearer ' + token }, withCredentials: true };

      if (billingInvoiceType === 'advance') {
        const advanceAmount = parseFloat(order.advance_paid) || 0;
        if (advanceAmount <= 0) {
          toast.error('Câmpul Avans trebuie completat cu suma (RON) pentru factura de avans');
          setCreatingInvoice(false);
          return;
        }
        await axios.post(API + '/admin/billing/invoices', {
          order_id: orderId,
          client_id: order.client_id,
          invoice_type: 'advance',
          due_date: dueDate,
          lines: [{ description: `Avans comandă ${order.order_number || orderId}`, quantity: 1, unit_price_eur: 0 }],
          advance_amount_ron: advanceAmount,
          exchange_rate_margin: 1,
          is_draft: false
        }, headers);
      } else {
        // Use edited invoice lines
        const lines = invoiceLines.map(l => ({
          service_id: l.service_id,
          description: l.description,
          quantity: parseFloat(l.quantity) || 1,
          unit_price_ron: parseFloat(l.unit_price_ron) || 0
        }));
        if (!lines.length || lines.every(l => l.unit_price_ron === 0)) {
          toast.error('Adăugați cel puțin o linie cu preț');
          setCreatingInvoice(false);
          return;
        }
        await axios.post(API + '/admin/billing/invoices', {
          order_id: orderId,
          client_id: order.client_id,
          invoice_type: billingInvoiceType,
          due_date: dueDate,
          lines,
          exchange_rate_margin: 1,
          is_draft: false,
          update_order_price: true
        }, headers);
      }
      toast.success('Factură creată cu succes');
      setInvoiceDialogOpen(false);
      fetchBillingData();
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la creare factură');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const openUploadDialog = (vehicleId) => {
    setCurrentVehicleId(vehicleId);
    setSelectedFile(null);
    setSelectedDocType('');
    setUploadDialogOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Fisierul este prea mare (max 10MB)');
        return;
      }
      setSelectedFile(file);
    }
  };

  const uploadDocument = async () => {
    if (!selectedFile || !selectedDocType) {
      toast.error('Selectati fisierul si tipul documentului');
      return;
    }
    setUploadingDoc(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('vehicle_id', currentVehicleId);
      formData.append('document_type', selectedDocType);
      await axios.post(API + '/admin/orders/' + orderId + '/documents', formData, {
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      toast.success('Document incarcat cu succes');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setSelectedDocType('');
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la incarcare');
    } finally {
      setUploadingDoc(false);
    }
  };

  const deleteDocument = async (docId) => {
    if (!confirm('Sigur doriti sa stergeti acest document?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(API + '/admin/orders/' + orderId + '/documents/' + docId, {
        headers: { Authorization: 'Bearer ' + token }, withCredentials: true
      });
      toast.success('Document sters');
      fetchOrder();
    } catch (error) {
      toast.error('Eroare');
    }
  };

  const downloadDocument = (doc) => {
    try {
      const byteCharacters = atob(doc.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: doc.content_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Eroare la descarcare');
    }
  };

  const generateDocument = async () => {
    if (!selectedTemplate) { toast.error('Selectati un template'); return; }
    setGeneratingDoc(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(API + '/admin/orders/' + orderId + '/generate-document', { template_name: selectedTemplate }, {
        headers: { Authorization: 'Bearer ' + token }, withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = selectedTemplate.endsWith('.docx') ? '.docx' : '.doc';
      const templateName = templates.find(t => t.value === selectedTemplate)?.label || 'Document';
      link.setAttribute('download', `${templateName.replace(/ /g, '_')}_${order.client?.name || 'Client'}${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Document generat cu succes');
      setTemplateDialogOpen(false);
      setSelectedTemplate('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la generarea documentului');
    } finally {
      setGeneratingDoc(false);
    }
  };

  const openTemplateDialog = () => { setSelectedTemplate(''); setTemplateDialogOpen(true); };
  const openFolderDialog = () => { setSelectedFolderType(''); setFolderDialogOpen(true); };

  const generateFolder = async () => {
    if (!selectedFolderType) { toast.error('Selectati un tip de dosar'); return; }
    setGeneratingFolder(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(API + '/admin/orders/' + orderId + '/generate-folder', { folder_type: selectedFolderType }, {
        headers: { Authorization: 'Bearer ' + token }, withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const folderName = folderTypes.find(t => t.value === selectedFolderType)?.label || 'Dosar';
      link.setAttribute('download', `${folderName.replace(/ /g, '_')}_${order.client?.name || 'Client'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Dosar generat cu succes');
      setFolderDialogOpen(false);
      setSelectedFolderType('');
    } catch (error) {
      if (error.response?.status === 501) toast.error('Functionalitate in curs de implementare');
      else toast.error(error.response?.data?.detail || 'Eroare la generarea dosarului');
    } finally {
      setGeneratingFolder(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  if (!order) return <div className="text-center py-12">Comanda negasita</div>;

  const isBuyerPF = editContract.buyer_type === 'persoana_fizica';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 flex-wrap">
        <Link to="/admin/orders"><Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-2" />Inapoi</Button></Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">{order.order_number}</h1>
            <Badge variant="secondary">{statusLabels[order.status] || order.status}</Badge>
          </div>
          <p className="text-slate-500">{new Date(order.created_at).toLocaleDateString('ro-RO')}</p>
        </div>

        <Button onClick={openEditDialog} variant="outline" className="gap-2" data-testid="edit-order-btn">
          <Pencil className="h-4 w-4" />
          Editeaza comanda
        </Button>

        <Button onClick={openTemplateDialog} className="bg-green-600 hover:bg-green-700 text-white">
          <FileText className="h-4 w-4 mr-2" />
          Genereaza document
        </Button>
        <Button onClick={openFolderDialog} className="bg-purple-600 hover:bg-purple-700 text-white">
          <FileText className="h-4 w-4 mr-2" />
          Genereaza dosar
        </Button>
        <Button onClick={() => { const token = localStorage.getItem('admin_token'); window.open(API + '/admin/orders/' + orderId + '/offer-pdf?token=' + token, '_blank'); }} variant="outline" className="gap-2" data-testid="offer-pdf-btn">
          <FileText className="h-4 w-4" />
          Oferta client
        </Button>

        {/* Edit Order Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editeaza comanda {order.order_number}</DialogTitle>
              <DialogDescription>Modifica datele comenzii</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm(prev => ({ ...prev, status: v }))}>
                    <SelectTrigger data-testid="edit-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(statusLabels).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Termen limita</Label>
                  <Input type="date" value={editForm.deadline} onChange={(e) => setEditForm(prev => ({ ...prev, deadline: e.target.value }))} data-testid="edit-deadline" />
                </div>
                <div>
                  <Label>Avans platit (RON)</Label>
                  <Input type="number" value={editForm.advance_paid} onChange={(e) => setEditForm(prev => ({ ...prev, advance_paid: e.target.value }))} data-testid="edit-advance" />
                </div>
              </div>
              <div>
                <Label>Notite</Label>
                <Textarea value={editForm.notes} onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))} rows={3} data-testid="edit-notes" />
              </div>

              {/* Client Edit */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <User className="h-5 w-5 text-blue-600" />
                  <Label className="text-base font-semibold text-blue-800">Date client (vanzator)</Label>
                </div>
                <div>
                  <Label className="text-sm font-medium">Selecteaza alt client</Label>
                  <Select value={selectedClientId} onValueChange={onClientSwitch}>
                    <SelectTrigger data-testid="edit-switch-client"><SelectValue placeholder="Selecteaza client..." /></SelectTrigger>
                    <SelectContent>
                      {allClients.map(c => (
                        <SelectItem key={c.client_id} value={c.client_id}>{c.name} - {c.cnp_cui}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">{editClient.client_type === 'persoana_fizica' ? 'Nume complet' : 'Denumire firma'}</Label>
                    <Input value={editClient.name} onChange={(e) => setEditClient(prev => ({ ...prev, name: e.target.value }))} data-testid="edit-client-name" />
                  </div>
                  <div>
                    <Label className="text-sm">{editClient.client_type === 'persoana_fizica' ? 'CNP' : 'CUI'}</Label>
                    <Input value={editClient.cnp_cui} onChange={(e) => setEditClient(prev => ({ ...prev, cnp_cui: e.target.value }))} data-testid="edit-client-cnp" />
                  </div>
                  <div>
                    <Label className="text-sm">{editClient.client_type === 'persoana_fizica' ? 'Serie Numar CI' : 'Nr. Reg. Comertului'}</Label>
                    <Input value={editClient.id_document} onChange={(e) => setEditClient(prev => ({ ...prev, id_document: e.target.value }))} data-testid="edit-client-id-doc" />
                  </div>
                  <div>
                    <Label className="text-sm">Telefon</Label>
                    <Input value={editClient.phone} onChange={(e) => setEditClient(prev => ({ ...prev, phone: e.target.value }))} data-testid="edit-client-phone" />
                  </div>
                  <div>
                    <Label className="text-sm">Email</Label>
                    <Input value={editClient.email} onChange={(e) => setEditClient(prev => ({ ...prev, email: e.target.value }))} data-testid="edit-client-email" />
                  </div>
                  <div>
                    <Label className="text-sm">Adresa</Label>
                    <Input value={editClient.address} onChange={(e) => setEditClient(prev => ({ ...prev, address: e.target.value }))} data-testid="edit-client-address" />
                  </div>
                  {editClient.client_type === 'persoana_juridica' && (
                    <div>
                      <Label className="text-sm">Administrator</Label>
                      <Input value={editClient.administrator || ''} onChange={(e) => setEditClient(prev => ({ ...prev, administrator: e.target.value }))} data-testid="edit-client-administrator" />
                    </div>
                  )}
                </div>
              </div>

              {/* Vehicle Edit */}
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <Car className="h-5 w-5 text-slate-600" />
                  <Label className="text-base font-semibold text-slate-800">Date vehicul</Label>
                </div>
                <div>
                  <Label className="text-sm font-medium">Selecteaza alt vehicul</Label>
                  <Select value={selectedVehicleId} onValueChange={onVehicleSwitch}>
                    <SelectTrigger data-testid="edit-switch-vehicle"><SelectValue placeholder="Selecteaza vehicul..." /></SelectTrigger>
                    <SelectContent>
                      {allVehicles.map(v => (
                        <SelectItem key={v.vehicle_id} value={v.vehicle_id}>{v.brand} {v.model} - {v.vin}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Serie sasiu (VIN)</Label>
                    <Input value={editVehicle.vin} onChange={(e) => setEditVehicle(prev => ({ ...prev, vin: e.target.value }))} data-testid="edit-vehicle-vin" />
                  </div>
                  <div>
                    <Label className="text-sm">Marca</Label>
                    <Input value={editVehicle.brand} onChange={(e) => setEditVehicle(prev => ({ ...prev, brand: e.target.value }))} data-testid="edit-vehicle-brand" />
                  </div>
                  <div>
                    <Label className="text-sm">Model</Label>
                    <Input value={editVehicle.model} onChange={(e) => setEditVehicle(prev => ({ ...prev, model: e.target.value }))} data-testid="edit-vehicle-model" />
                  </div>
                  <div>
                    <Label className="text-sm">Nr. inmatriculare</Label>
                    <Input value={editVehicle.plate_number} onChange={(e) => setEditVehicle(prev => ({ ...prev, plate_number: e.target.value }))} data-testid="edit-vehicle-plate" />
                  </div>
                  <div>
                    <Label className="text-sm">An fabricatie</Label>
                    <Input type="number" value={editVehicle.year} onChange={(e) => setEditVehicle(prev => ({ ...prev, year: e.target.value }))} data-testid="edit-vehicle-year" />
                  </div>
                  <div>
                    <Label className="text-sm">Serie motor</Label>
                    <Input value={editVehicle.serie_motor} onChange={(e) => setEditVehicle(prev => ({ ...prev, serie_motor: e.target.value }))} data-testid="edit-vehicle-serie-motor" />
                  </div>
                  <div>
                    <Label className="text-sm">Capacitate cilindrica (cc)</Label>
                    <Input type="number" value={editVehicle.capacitate_cilindrica} onChange={(e) => setEditVehicle(prev => ({ ...prev, capacitate_cilindrica: e.target.value }))} data-testid="edit-vehicle-cc" />
                  </div>
                </div>
              </div>

              {/* Contract Data Edit */}
              {showContractSection && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <HandshakeIcon className="h-5 w-5 text-amber-600" />
                    <Label className="text-base font-semibold text-amber-800">Date contract vanzare-cumparare</Label>
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Selecteaza cumparator din clienti existenti</Label>
                    <Select
                      value="__none__"
                      onValueChange={(clientId) => {
                        if (clientId === '__none__') return;
                        const c = allClients.find(cl => cl.client_id === clientId);
                        if (c) {
                          setEditContract(prev => ({
                            ...prev,
                            buyer_type: c.client_type || 'persoana_fizica',
                            buyer_name: c.name || '',
                            buyer_cnp_cui: c.cnp_cui || '',
                            buyer_id_document: c.id_document || '',
                            buyer_address: c.address || ''
                          }));
                        }
                      }}
                    >
                      <SelectTrigger data-testid="edit-select-buyer-from-clients">
                        <SelectValue placeholder="Selecteaza client existent..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- Completare manuala --</SelectItem>
                        {allClients.map(c => (
                          <SelectItem key={c.client_id} value={c.client_id}>{c.name} - {c.cnp_cui}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm">Tip cumparator</Label>
                    <Select value={editContract.buyer_type} onValueChange={(v) => setEditContract(prev => ({ ...prev, buyer_type: v }))}>
                      <SelectTrigger data-testid="edit-buyer-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="persoana_fizica">Persoana fizica</SelectItem>
                        <SelectItem value="persoana_juridica">Persoana juridica</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">{isBuyerPF ? 'Nume complet cumparator' : 'Denumire firma cumparator'}</Label>
                      <Input value={editContract.buyer_name} onChange={(e) => setEditContract(prev => ({ ...prev, buyer_name: e.target.value }))} data-testid="edit-buyer-name" />
                    </div>
                    <div>
                      <Label className="text-sm">{isBuyerPF ? 'CNP cumparator' : 'CUI'}</Label>
                      {!isBuyerPF ? (
                        <CUILookupInput
                          value={editContract.buyer_cnp_cui}
                          onChange={(e) => setEditContract(prev => ({ ...prev, buyer_cnp_cui: e.target.value }))}
                          onCompanyFound={(company) => setEditContract(prev => ({
                            ...prev,
                            buyer_name: company.name || prev.buyer_name,
                            buyer_cnp_cui: company.cui || prev.buyer_cnp_cui,
                            buyer_address: company.address || prev.buyer_address,
                            buyer_id_document: company.registration_number || prev.buyer_id_document
                          }))}
                          data-testid="edit-buyer-cnp"
                        />
                      ) : (
                        <Input value={editContract.buyer_cnp_cui} onChange={(e) => setEditContract(prev => ({ ...prev, buyer_cnp_cui: e.target.value }))} data-testid="edit-buyer-cnp" />
                      )}
                    </div>
                    <div>
                      <Label className="text-sm">{isBuyerPF ? 'Serie Numar CI Cumparator' : 'Nr. Reg. Comertului Cumparator'}</Label>
                      <Input value={editContract.buyer_id_document} onChange={(e) => setEditContract(prev => ({ ...prev, buyer_id_document: e.target.value }))} data-testid="edit-buyer-id-doc" />
                    </div>
                    <div>
                      <Label className="text-sm">Localitate intocmire document</Label>
                      <Select
                        value={ILFOV_LOCALITIES.includes(editContract.localitate) ? editContract.localitate : '__manual__'}
                        onValueChange={(v) => { if (v !== '__manual__') setEditContract(prev => ({ ...prev, localitate: v })); }}
                      >
                        <SelectTrigger data-testid="edit-localitate"><SelectValue placeholder="Selectati..." /></SelectTrigger>
                        <SelectContent>
                          {ILFOV_LOCALITIES.map((loc) => (<SelectItem key={loc} value={loc}>{loc}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={ILFOV_LOCALITIES.includes(editContract.localitate) ? '' : editContract.localitate}
                        onChange={(e) => setEditContract(prev => ({ ...prev, localitate: e.target.value }))}
                        placeholder="Sau scrieti manual..."
                        className="mt-1"
                        data-testid="edit-localitate-manual"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-sm">Adresa cumparator</Label>
                      <Input value={editContract.buyer_address} onChange={(e) => setEditContract(prev => ({ ...prev, buyer_address: e.target.value }))} data-testid="edit-buyer-address" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Pret vanzare</Label>
                      <div className="flex gap-2">
                        <Input type="number" min="0" value={editContract.pret_vanzare} onChange={(e) => setEditContract(prev => ({ ...prev, pret_vanzare: e.target.value }))} className="flex-1" data-testid="edit-pret" />
                        <Select value={editContract.pret_vanzare_moneda} onValueChange={(v) => setEditContract(prev => ({ ...prev, pret_vanzare_moneda: v }))}>
                          <SelectTrigger className="w-24" data-testid="edit-moneda"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="RON">RON</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">Pret vanzare (text)</Label>
                      <div className="h-10 flex items-center px-3 bg-amber-100 border border-amber-300 rounded-md text-amber-900 font-medium text-sm" data-testid="edit-pret-text">
                        {editPretText || editContract.pret_vanzare_text || 'Completati pretul...'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Anuleaza</Button>
                <Button onClick={saveOrder} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-order-btn">
                  {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Se salveaza...</> : 'Salveaza modificarile'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Template Selection Dialog */}
        <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Selecteaza tipul documentului</DialogTitle>
              <DialogDescription>Alege template-ul pentru generare</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Tip document</Label>
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger><SelectValue placeholder="Selecteaza un document..." /></SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (<SelectItem key={template.value} value={template.value}>{template.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>Anuleaza</Button>
                <Button onClick={generateDocument} disabled={generatingDoc || !selectedTemplate} className="bg-green-600 hover:bg-green-700 text-white">
                  {generatingDoc ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Se genereaza...</> : <><FileText className="h-4 w-4 mr-2" />Genereaza</>}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Folder Dialog */}
        <Dialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Selecteaza tipul dosarului</DialogTitle>
              <DialogDescription>Alege tipul dosarului pentru generare</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Tip dosar</Label>
                <Select value={selectedFolderType} onValueChange={setSelectedFolderType}>
                  <SelectTrigger><SelectValue placeholder="Selecteaza un tip de dosar..." /></SelectTrigger>
                  <SelectContent>
                    {folderTypes.map((folder) => (<SelectItem key={folder.value} value={folder.value}>{folder.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setFolderDialogOpen(false)}>Anuleaza</Button>
                <Button onClick={generateFolder} disabled={generatingFolder || !selectedFolderType} className="bg-purple-600 hover:bg-purple-700 text-white">
                  {generatingFolder ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Se genereaza...</> : <><FileText className="h-4 w-4 mr-2" />Genereaza</>}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={invoiceDialogOpen} onOpenChange={(open) => { setInvoiceDialogOpen(open); if (open) initInvoiceLines(); }}>
          <DialogTrigger asChild><Button className="bg-blue-600 hover:bg-blue-700 text-white"><Receipt className="h-4 w-4 mr-2" />Facturare</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Emite factură</DialogTitle><DialogDescription>Editați sumele dacă este necesar, apoi emiteți factura</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tip factură</Label>
                  <Select value={billingInvoiceType} onValueChange={(v) => { setBillingInvoiceType(v); if (v !== 'advance') initInvoiceLines(); }}>
                    <SelectTrigger data-testid="billing-invoice-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normală</SelectItem>
                      <SelectItem value="advance">Avans</SelectItem>
                      <SelectItem value="final">Finală</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Data scadenței</Label><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="billing-due-date" /></div>
              </div>

              {billingInvoiceType !== 'advance' && invoiceLines.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Linii factură (editabile)</Label>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-2 font-medium text-slate-600">Descriere</th>
                          <th className="text-center p-2 font-medium text-slate-600 w-16">Cant.</th>
                          <th className="text-right p-2 font-medium text-slate-600 w-32">Preț RON</th>
                          <th className="text-right p-2 font-medium text-slate-600 w-28">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceLines.map((line, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="p-2">
                              <Input
                                value={line.description}
                                onChange={(e) => updateInvoiceLine(idx, 'description', e.target.value)}
                                className="h-8 text-sm"
                                data-testid={`invoice-line-desc-${idx}`}
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="number"
                                min="1"
                                value={line.quantity}
                                onChange={(e) => updateInvoiceLine(idx, 'quantity', e.target.value)}
                                className="h-8 text-sm text-center"
                                data-testid={`invoice-line-qty-${idx}`}
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="number"
                                step="0.01"
                                value={line.unit_price_ron}
                                onChange={(e) => updateInvoiceLine(idx, 'unit_price_ron', e.target.value)}
                                className="h-8 text-sm text-right"
                                data-testid={`invoice-line-price-${idx}`}
                              />
                            </td>
                            <td className="p-2 text-right font-medium text-slate-700">
                              {((parseFloat(line.unit_price_ron) || 0) * (parseFloat(line.quantity) || 0)).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="bg-slate-50 px-3 py-2 flex justify-between items-center border-t">
                      <span className="text-sm text-slate-500">Total: <strong className="text-slate-800">{invoiceTotal.toFixed(2)} RON</strong> ({invoiceTotalEur} EUR)</span>
                      <Button variant="ghost" size="sm" onClick={() => setInvoiceLines(prev => [...prev, { service_id: null, description: '', quantity: 1, unit_price_ron: 0 }])} className="text-xs h-7" data-testid="invoice-add-line-btn">+ Linie nouă</Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>Anulează</Button>
                <Button onClick={createInvoice} disabled={creatingInvoice} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="billing-create-btn">
                  {creatingInvoice ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Se creează...</> : 'Emite factură'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-white">
            <CardHeader><CardTitle className="flex items-center gap-2"><User className="h-5 w-5 text-blue-600" />Client</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div><p className="text-sm text-slate-500">Nume</p><p className="font-medium">{order.client?.name || 'N/A'}</p></div>
              <div><p className="text-sm text-slate-500">Email</p><p>{order.client?.email || 'N/A'}</p></div>
              <div><p className="text-sm text-slate-500">Telefon</p><p>{order.client?.phone || 'N/A'}</p></div>
              {order.client?.cnp_cui && <div><p className="text-sm text-slate-500">CNP/CUI</p><p>{order.client.cnp_cui}</p></div>}
              {order.client?.address && <div className="col-span-2"><p className="text-sm text-slate-500">Adresa</p><p>{order.client.address}</p></div>}
              {order.client?.client_type === 'persoana_juridica' && order.client?.administrator && (
                <div><p className="text-sm text-slate-500">Administrator</p><p className="font-medium">{order.client.administrator}</p></div>
              )}
            </CardContent>
          </Card>

          {showContractSection && (
            <Card className="bg-white border-amber-200" data-testid="contract-data-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <FileText className="h-5 w-5" />
                  Date contract vanzare-cumparare
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                {order.contract_data ? (<>
                <div>
                  <p className="text-sm text-slate-500">Tip cumparator</p>
                  <p className="font-medium">{order.contract_data.buyer_type === 'persoana_fizica' ? 'Persoana fizica' : 'Persoana juridica'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">
                    {order.contract_data.buyer_type === 'persoana_fizica' ? 'Nume cumparator' : 'Denumire firma'}
                  </p>
                  <p className="font-medium">{order.contract_data.buyer_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">
                    {order.contract_data.buyer_type === 'persoana_fizica' ? 'CNP cumparator' : 'CUI'}
                  </p>
                  <p className="font-medium">{order.contract_data.buyer_cnp_cui}</p>
                </div>
                {order.contract_data.buyer_id_document && (
                  <div>
                    <p className="text-sm text-slate-500">
                      {order.contract_data.buyer_type === 'persoana_fizica' ? 'Serie Numar CI' : 'Nr. Reg. Comertului'}
                    </p>
                    <p className="font-medium">{order.contract_data.buyer_id_document}</p>
                  </div>
                )}
                {order.contract_data.buyer_address && (
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">Adresa cumparator</p>
                    <p className="font-medium">{order.contract_data.buyer_address}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-500">Localitate</p>
                  <p className="font-medium">{order.contract_data.localitate}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pret vanzare</p>
                  <p className="font-medium">{order.contract_data.pret_vanzare} {order.contract_data.pret_vanzare_moneda}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-slate-500">Pret vanzare (text)</p>
                  <p className="font-medium text-amber-800">{order.contract_data.pret_vanzare_text}</p>
                </div>
                </>) : (
                  <div className="col-span-2 text-center py-4">
                    <p className="text-sm text-amber-600">Datele contractului nu au fost completate inca.</p>
                    <p className="text-xs text-slate-500 mt-1">Apasati "Editeaza" pentru a completa datele contractului de vanzare-cumparare.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="bg-white">
            <CardHeader><CardTitle className="flex items-center gap-2"><Car className="h-5 w-5 text-blue-600" />Vehicule</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {order.vehicles?.map((veh, idx) => (
                <div key={idx} className="p-4 bg-slate-50 rounded-lg space-y-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium">{veh.vehicle_details?.brand} {veh.vehicle_details?.model}</p>
                      <p className="text-sm text-slate-500">VIN: {veh.vehicle_details?.vin}</p>
                    </div>
                    <Select value={veh.status} onValueChange={(v) => updateVehicleStatus(veh.vehicle_id, v)}>
                      <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(statusLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {veh.service_details?.map((srv, i) => <Badge key={i} variant="secondary">{srv.name} - {srv.price} EUR</Badge>)}
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-sm flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Documente ({veh.documents?.length || 0})
                      </h4>
                      <Button size="sm" onClick={() => openUploadDialog(veh.vehicle_id)} className="bg-blue-600 hover:bg-blue-700 text-white">
                        <Upload className="h-4 w-4 mr-2" />
                        Incarca document
                      </Button>
                    </div>
                    {veh.documents && veh.documents.length > 0 ? (
                      <div className="space-y-2">
                        {veh.documents.map((doc) => (
                          <div key={doc.doc_id} className="flex items-center justify-between p-3 bg-white rounded border border-slate-200">
                            <div className="flex items-center gap-3 flex-1">
                              <FileText className="h-5 w-5 text-blue-600" />
                              <div className="flex-1">
                                <p className="text-sm font-medium">{doc.filename}</p>
                                <div className="flex items-center gap-3 text-xs text-slate-500">
                                  <span className="font-medium text-blue-600">{doc.document_type}</span>
                                  <span>{(doc.size / 1024).toFixed(1)} KB</span>
                                  <span>{new Date(doc.uploaded_at).toLocaleDateString('ro-RO')}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button size="sm" variant="outline" onClick={() => downloadDocument(doc)}><Download className="h-4 w-4" /></Button>
                              <Button size="sm" variant="outline" onClick={() => deleteDocument(doc.doc_id)} className="text-red-600 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-4">Niciun document incarcat</p>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Upload Document Dialog */}
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Incarca document</DialogTitle>
                <DialogDescription>Selecteaza tipul si fisierul de incarcat</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Tip document</Label>
                  <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                    <SelectTrigger><SelectValue placeholder="Selecteaza tipul documentului" /></SelectTrigger>
                    <SelectContent>{DOCUMENT_TYPES.map((type) => (<SelectItem key={type} value={type}>{type}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fisier (max 10MB)</Label>
                  <Input type="file" onChange={handleFileChange} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                  {selectedFile && <p className="text-sm text-slate-500 mt-2">Selectat: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)</p>}
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Anuleaza</Button>
                  <Button onClick={uploadDocument} disabled={uploadingDoc || !selectedFile || !selectedDocType} className="bg-blue-600 hover:bg-blue-700 text-white">
                    {uploadingDoc ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Se incarca...</> : <><Upload className="h-4 w-4 mr-2" />Incarca</>}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Billing Section */}
          <Card className="bg-white" data-testid="order-billing-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-blue-600" />Facturare</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {billingData && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-lg font-bold text-blue-700">{billingData.total_invoiced_ron?.toFixed(2)}</p>
                    <p className="text-xs text-blue-600">Facturat RON</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg text-center">
                    <p className="text-lg font-bold text-amber-700">{billingData.total_advance_ron?.toFixed(2)}</p>
                    <p className="text-xs text-amber-600">Avans RON</p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-lg text-center">
                    <p className="text-lg font-bold text-emerald-700">{billingData.total_paid_ron?.toFixed(2)}</p>
                    <p className="text-xs text-emerald-600">Încasat RON</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg text-center">
                    <p className="text-lg font-bold text-red-700">{billingData.remaining_ron?.toFixed(2)}</p>
                    <p className="text-xs text-red-600">Rest de plată</p>
                  </div>
                </div>
              )}
              {billingData?.invoices?.length > 0 ? (
                <div className="space-y-2">
                  {billingData.invoices.map((inv) => (
                    <Link key={inv.invoice_id} to={`/admin/invoices/${inv.invoice_id}`} className="block p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium text-sm">{inv.invoice_number || 'Draft'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">{inv.invoice_type === 'advance' ? 'Avans' : inv.invoice_type === 'final' ? 'Finală' : inv.invoice_type === 'storno' ? 'Storno' : 'Normală'}</Badge>
                            <span className="text-xs text-slate-500">{inv.issue_date}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${inv.is_storno ? 'text-red-600' : ''}`}>{inv.total_ron?.toFixed(2)} RON</p>
                          <Badge variant="outline" className={`text-xs ${inv.payment_status === 'paid' ? 'text-emerald-600 border-emerald-300' : inv.payment_status === 'partially_paid' ? 'text-blue-600 border-blue-300' : 'text-amber-600 border-amber-300'}`}>
                            {inv.payment_status === 'paid' ? 'Plătită' : inv.payment_status === 'partially_paid' ? 'Parțial' : inv.payment_status === 'cancelled' ? 'Anulată' : 'Neplătită'}
                          </Badge>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">Nicio factură emisă pentru această comandă</p>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="bg-white h-fit">
          <CardHeader><CardTitle>Sumar</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="font-bold text-xl">{order.total_price} EUR</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Avans</span><span className="text-emerald-600">{order.advance_paid} RON</span></div>
            <div className="flex justify-between border-t pt-4"><span className="text-slate-500">Rest</span><span className="font-bold">{order.total_price - order.advance_paid} EUR</span></div>
            {order.notes && (
              <div className="border-t pt-4">
                <p className="text-sm text-slate-500">Notite</p>
                <p className="text-sm">{order.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
